import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import { LocaleContext } from "../../../routing/LangRouter";
import axios from "axios";
import BASE_URL from "../../../constants/baseUrls";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { debouncer, navigateToLoginPage } from "../../../utils/utility";
import { BREAKPOINT, IS_MOBILE, WIDTH } from "../../../constants/breakpoints";

//import general components
import BaseDialog from "../../../components/common/base-dialog/base-dialog";
import BaseTitle from "../../../components/common/base-title/base-title";
import EmptyOrErrorState from "../../../components/common/empty-or-error-state/empty-or-error-state";
import BasePagination from "../../../components/common/base-pagination/base-pagination";

//import view components
import FlightOrders from "../../../components/view/user-panel/orders/flight-orders";
import AccommodationOrders from "../../../components/view/user-panel/orders/accommodation-orders";
import OrderFilter from "../../../components/view/user-panel/orders/order-filter";
import OrderSkeleton from "../../../components/view/user-panel/orders/order-skeleton";
import OrdersCategory from "../../../components/view/user-panel/orders/orders-category";
import FlightDetailsModalContent from "../../../components/view/user-panel/orders/flight-details-modal-content";
import AccommodationDetailsModalContent from "../../../components/view/user-panel/orders/accommodation-details-modal-content";

//import images and icons
import { InfoCircle } from "iconsax-react";

//import styles
import "./orders.scss";
import BusItem from "../../../components/view/bus/bus-item";
import PurchasedBusTicket from "../../../components/view/bus/purchasedBusTicket";

function Orders() {
    //general variables
    const navigate = useNavigate();
    const location = useLocation();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const accessToken = localStorage.getItem("accessToken");

    //handle pagination
    const [orderList, setOrderList] = useState([]);
    const [page, setPage] = useState(orderList?.current_page ?? 1);
    const handlePaginationChange = async (event, value) => {
        setPage(value);
        getOrderList(value);
    };

    //handle filters
    //selected category
    const [selectedCategory, setSelectedCategory] = useState(1);
    const onCategoryChange = value => {
        debouncedOnChange(value);
        setSelectedCategory(value);
    };
    const debouncedOnChange = useMemo(
        () =>
            debouncer(input => {
                setLoading(true);
                getOrderList(page, input);
            }, 500),
        [],
    );

    //status changes
    const statusObject = { paymentSuccess: 8, cancelled: 12, preReserve: 4 };
    const handleStatusFilterChange = values => {
        if (!!values) {
            const statusFilterId = Object.keys(values)
                .map(item => {
                    if (values[item] === true) {
                        return statusObject[item];
                    } else return 0;
                })
                .filter(item => item !== 0)
                ?.join(",");
            return statusFilterId;
        }
    };

    //filter submit
    const handleFilterChange = (statusValues, fromDate, toDate) => {
        const status = handleStatusFilterChange(statusValues);
        getOrderList(page, selectedCategory, status, fromDate, toDate);
    };

    //request order list
    const getOrderList = async (page, category, status, fromDate, toDate) => {
        setOrderList([]);
        try {
            setLoading(true);
            const response = await axios.get(`${BASE_URL}/api/v2/account/user/reserve/list/`, {
                params: {
                    page,
                    type: category || selectedCategory,
                    status,
                    from_date: fromDate,
                    to_date: toDate,
                },
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            setOrderList(response.data.data);
            setLoading(false);
        } catch (error) {
            if (error.response.status === 401) {
                localStorage.clear();
                navigateToLoginPage(navigate, locale, location);
            } else {
                toast.error(t("Toast Messages.Error Occurred"), { toastId: "custom-id-yes" });
            }
            setLoading(false);
        }
    };

    useEffect(() => {
        getOrderList(page);
    }, []);

    //handle details Modal
    const [detailsModal, setDetailsModal] = useState({
        flight: false,
        accommodation: false,
        isHotelInternational: false,
        isFlightInternational: false,
        selectedOrderItem: null,
    });
    const handleOpenDetailsModal = ({ type, isHotelInternational, isFlightInternational, selectedOrderItem }) => {
        setDetailsModal(values => ({
            ...values,
            [type]: true,
            isHotelInternational,
            isFlightInternational,
            selectedOrderItem,
        }));
    };

    const handleCloseDetailsModal = () => {
        setDetailsModal({ flight: false, accommodation: false, isHotelInternational: false, isFlightInternational: false, selectedOrderItem: null });
    };

    return (
        <Grid className="orders" container item xs={12} rowGap={2} justifyContent="center" mb={5}>
            {!IS_MOBILE && <BaseTitle title={`${t("orders")}`} dividerColor="1px solid var(--medium-gray-color)" />}

            <OrdersCategory value={selectedCategory} setValue={onCategoryChange} loading={loading} />

            {WIDTH > BREAKPOINT && (
                <Grid container item xs={12}>
                    <OrderFilter submit={handleFilterChange} />
                </Grid>
            )}

            {loading ? (
                <OrderSkeleton />
            ) : orderList?.results?.length ? (
                <>
                    {orderList?.results?.map(orderItem => {
                        return (
                            <Grid key={Math.random()} container item xs={12} rowGap={{ xs: 2, md: 5 }}>
                                {selectedCategory === 1 || selectedCategory === 2 ? (
                                    !!orderItem?.flight && (
                                        <FlightOrders
                                            orderItem={orderItem}
                                            handleClick={(type, isFlightInternational, selectedOrderItem) =>
                                                handleOpenDetailsModal({ type, isFlightInternational, selectedOrderItem })
                                            }
                                        />
                                    )
                                ) : selectedCategory === 3 || selectedCategory === 4 ? (
                                    !!orderItem?.accommodation && (
                                        <AccommodationOrders
                                            orderItem={orderItem}
                                            handleClick={(type, isHotelInternational, selectedOrderItem) =>
                                                handleOpenDetailsModal({ type, isHotelInternational, selectedOrderItem })
                                            }
                                        />
                                    )
                                ) : selectedCategory === 5 ? (
                                    <Grid container item xs={12} rowGap={2}>
                                        <PurchasedBusTicket
                                            labels={[orderItem?.bus?.bus_code, `شماره پیگیری: ${orderItem?.tracking_code}`]}
                                            logoUrl="/logos/company1.png"
                                            companyName={orderItem?.bus?.company}
                                            ticketDate={orderItem?.bus?.move_date.replaceAll("-", "/")}
                                            departureTime={orderItem?.bus?.move_time}
                                            origin={orderItem?.bus?.origin_city?.name}
                                            destination={orderItem?.bus?.destination_city?.name}
                                            price={orderItem?.bus?.price}
                                            ticketNumber={orderItem?.tracking_code}
                                            passengers={orderItem?.bus?.passengers}
                                        />
                                    </Grid>
                                ) : null}
                            </Grid>
                        );
                    })}
                    <BasePagination count={orderList?.total_pages} page={orderList?.current_page ?? 1} handleChange={handlePaginationChange} />
                </>
            ) : (
                !loading && (
                    <EmptyOrErrorState title={t("noOrdersYet")} icon={<InfoCircle size="32" color="var(--secondary-color)" variant="Bold" />} />
                )
            )}

            <BaseDialog open={detailsModal.flight} handleClose={handleCloseDetailsModal} maxWidth="lg" top="0" icon="close">
                {detailsModal?.flight && (
                    <FlightDetailsModalContent selectedOrderItem={detailsModal?.selectedOrderItem} handleClose={handleCloseDetailsModal} />
                )}
            </BaseDialog>

            <BaseDialog open={detailsModal.accommodation} handleClose={handleCloseDetailsModal} maxWidth="md" top="0" icon="close">
                {detailsModal?.accommodation && (
                    <AccommodationDetailsModalContent selectedOrderItem={detailsModal?.selectedOrderItem} handleClose={handleCloseDetailsModal} />
                )}
            </BaseDialog>
        </Grid>
    );
}

export default Orders;
